import { useSearchParams } from 'react-router-dom';
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  IconButton,
  Popover,
  Chip,
  LinearProgress,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  EmojiEmotions as EmojiIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { SocialPlatform, PlatformConnection } from '../types';
import { useAuth } from '../contexts/AuthContext';
import {
  availablePlatforms,
  getPlatform,
  getPlatformColor,
  getPlatformName,
  strictestCharLimit,
} from '../config/platforms';
import { InstagramImageCheck } from '../components/InstagramImageCheck';
import { PostPreview } from '../components/PostPreview';
import { GuidedPost } from '../components/GuidedPost';
import { PlatformSchedule, ScheduleVariants, pastScheduleEntries } from '../components/PlatformSchedule';
import { IdeaPicker, StagedIdea } from '../components/IdeaPicker';
import { PerPlatformCopy, textFor as resolveText } from '../components/PerPlatformCopy';
import { thumbnailUrl } from '../utils/media';

const API_URL = process.env.REACT_APP_API_URL || '';

export const CreatePost: React.FC = () => {
  const { token, user } = useAuth();
  // Manual by default. Guided is the newer path and derives the day, time and platforms from
  // the posting schedule, but the composer is what people already know — so it stays the
  // landing state and guided is opt-in.
  const [mode, setMode] = useState<'guided' | 'manual'>('manual');
  /**
   * The staged idea this post was started from, if any.
   *
   * Held so saving updates that post rather than creating a second one — the idea becomes the
   * scheduled post and leaves staging by itself, which is the whole point of staging being
   * drafts rather than a separate table.
   */
  const [fromIdea, setFromIdea] = useState<string | null>(null);
  // The idea as loaded, so saving it cannot overwrite a change made elsewhere meanwhile
  const [ideaVersion, setIdeaVersion] = useState<string | null>(null);
  /**
   * Times a staged idea kept from when it was last scheduled. Sent back when it is saved as a
   * draft again, because the PUT replaces every column and would otherwise clear them.
   */
  const [ideaTimes, setIdeaTimes] = useState<{
    scheduledFor: string | null;
    scheduleVariants: Record<string, string> | null;
  }>({ scheduledFor: null, scheduleVariants: null });

  // Arriving from Idea Staging with ?idea=<id>: load it straight in, so "Choose networks"
  // lands on a filled composer rather than an empty one.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('idea');
    if (!id || !token || fromIdea === id) return;
    fetch(`${API_URL}/api/posts/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(idea => {
        if (!idea) return;
        setFromIdea(idea.id);
        setIdeaVersion(idea.version || null);
        setIdeaTimes({
          scheduledFor: idea.scheduled_for || null,
          scheduleVariants: idea.schedule_variants || null,
        });
        setContent(idea.content || '');
        setSelectedPlatforms((idea.platforms || []) as SocialPlatform[]);
        setVariants(idea.content_variants || {});
        setMedia((idea.media_urls || []).map((url: string) => ({
          id: url, url, mimeType: '', sizeBytes: 0,
        })));
      })
      .catch(() => { /* a failed load leaves an empty composer, which still works */ });
  }, [searchParams, token, fromIdea]);
  const [content, setContent] = useState('');
  /**
   * Per-platform caption overrides, keyed by platform id.
   *
   * X allows 280 characters where the others allow 2,200 to 63,206, so a post that is
   * comfortable everywhere else is eight times over on X. Rather than forcing the whole post
   * down to the shortest limit, a platform can carry its own shorter text and the rest keep
   * the full caption.
   */
  const [variants, setVariants] = useState<Record<string, string>>({});
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  /**
   * Per-network times, when "Use a different time for each network" is on. Empty means every
   * network publishes at `scheduledDate`. Until v1.20.0 this was only reachable by editing the
   * post after creating it.
   */
  const [scheduleVariants, setScheduleVariants] = useState<ScheduleVariants>({});
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [media, setMedia] = useState<{ id: string; url: string; mimeType: string; sizeBytes: number }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [targets, setTargets] = useState<Record<string, string | null>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Only platforms that can actually publish are offered, so a post cannot be
  // created targeting an integration that does not exist.
  // A demo account is restricted server-side, so do not offer what would be refused
  const platforms: { value: SocialPlatform; label: string }[] = availablePlatforms()
    .filter(p => !user?.allowedPlatforms || user.allowedPlatforms.includes(p.id))
    .map(p => ({ value: p.id, label: p.name }));

  // Fetch connections on mount
  useEffect(() => {
    const loadConnections = async () => {
      try {
        const response = await fetch(`${API_URL}/api/connections`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setConnections(data);
        }
      } catch (err) {
        console.error('Failed to load connections:', err);
      }
    };

    loadConnections();

    fetch(`${API_URL}/api/platforms`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!data) return;
        setTargets(Object.fromEntries(
          data.platforms.map((p: { platform: string; target: string | null }) => [p.platform, p.target])
        ));
      })
      .catch(() => { /* preview falls back to the platform name */ });
  }, [token]);

  // Get LinkedIn posting destination info
  const getLinkedInDestination = () => {
    const linkedInConn = connections.find(conn => conn.platform === 'linkedin' && conn.isActive);
    if (!linkedInConn) return null;

    const defaultOrg = linkedInConn.organizations?.find(org => org.isDefault);
    if (defaultOrg) {
      return `Company Page: ${defaultOrg.organizationName}`;
    }
    return `Personal Profile: ${linkedInConn.accountName}`;
  };

  const handlePlatformChange = (platform: SocialPlatform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter((p) => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const textarea = textFieldRef.current;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = content.substring(0, start) + emoji + content.substring(end);
      setContent(newContent);

      // Set cursor position after emoji
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    } else {
      setContent(content + emoji);
    }

    setEmojiAnchorEl(null);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear the input so picking the same file twice still fires a change event
    event.target.value = '';
    if (!file) return;

    setError('');
    setUploading(true);

    try {
      const response = await fetch(`${API_URL}/api/media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': file.type,
        },
        body: file,
      });

      const data = await response.json();

      if (response.ok) {
        setMedia(prev => [...prev, data]);
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      setError('Network error uploading file');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveMedia = async (id: string) => {
    setMedia(prev => prev.filter(m => m.id !== id));
    // Best effort: the post no longer references it, so a failure here only leaves an
    // orphaned file for housekeeping to reap.
    try {
      await fetch(`${API_URL}/api/media/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch (err) {
      // ignore
    }
  };

  const handleSubmit = async (action: 'save' | 'schedule' | 'publish') => {
    setError('');
    setSuccess('');

    // An idea needs something in it, but text or media will do — a picture with the caption
    // still to write is a perfectly good thing to stage.
    if (!content.trim() && media.length === 0) {
      setError('Add some text or media before saving');
      return;
    }

    // Saving to staging needs no platform; scheduling or publishing does, because a post
    // cannot go out to nowhere.
    if (action !== 'save' && selectedPlatforms.length === 0) {
      setError('Please select at least one platform');
      return;
    }

    if (action === 'schedule' && !scheduledDate) {
      setError('Please select a date and time to schedule the post');
      return;
    }

    const pastPlatforms = action === 'schedule'
      ? pastScheduleEntries(selectedPlatforms, scheduleVariants)
      : [];
    if (pastPlatforms.length > 0) {
      setError(`The time for ${pastPlatforms.map(p => getPlatformName(p as SocialPlatform)).join(', ')} has already passed. Choose a future time.`);
      return;
    }

    // Block rather than let the platform reject it later
    if (overLimitPlatforms.length > 0) {
      const names = overLimitPlatforms.map(p => p.name).join(', ');
      setError(
        `Too long for ${overLimitPlatforms.map(p => `${p.name} (${p.charLimit})`).join(', ')}. ` +
        `Write a shorter ${names} version below — the other platforms keep the full caption ` +
        '— or deselect it.'
      );
      return;
    }

    if (missingMediaPlatforms.length > 0) {
      setError(`${missingMediaPlatforms.map(p => p.name).join(', ')} requires an image or video — add one below.`);
      return;
    }

    setSubmitting(true);

    try {
      // Started from a staged idea: update that post rather than creating a second one. The
      // idea becomes the scheduled post and leaves staging by itself, which is the point of
      // staging being drafts rather than a separate table.
      const createResponse = await fetch(
        fromIdea ? `${API_URL}/api/posts/${fromIdea}` : `${API_URL}/api/posts`,
        {
        method: fromIdea ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content,
          // Only non-empty overrides: an emptied box means "use the main caption", and the
          // server treats a blank the same way.
          contentVariants: Object.fromEntries(
            Object.entries(variants).filter(([, text]) => text.trim())
          ),
          platforms: selectedPlatforms,
          mediaUrls: media.map(m => m.url),
          // Scheduling sends the time chosen here, plus a time per network when they differ.
          // Saving a staged idea back as a draft keeps whatever times it already had.
          ...(action === 'schedule'
            ? {
                scheduledFor: scheduledDate ? scheduledDate.toISOString() : null,
                // Only the selected networks: a time for one that was unticked would be
                // refused by the server as a target the post does not have.
                scheduleVariants: Object.keys(scheduleVariants).length
                  ? Object.fromEntries(
                      Object.entries(scheduleVariants)
                        .filter(([p]) => selectedPlatforms.includes(p as SocialPlatform))
                    )
                  : null,
              }
            : fromIdea
              ? ideaTimes
              : { scheduledFor: null }),
          status: action === 'schedule' ? 'scheduled' : 'draft',
          ...(fromIdea && ideaVersion ? { version: ideaVersion } : {}),
        }),
      }
      );

      const created = await createResponse.json();

      if (!createResponse.ok) {
        setError(created.error || 'Failed to create post');
        return;
      }

      if (action === 'publish') {
        const publishResponse = await fetch(`${API_URL}/api/posts/${created.id}/publish`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        const published = await publishResponse.json();

        if (!publishResponse.ok) {
          setError(published.error || 'Post was saved but publishing failed');
          return;
        }

        // Report per-platform outcomes rather than a blanket success
        const results = published.results || [];
        const failed = results.filter((r: { status: string }) => r.status === 'failed');

        if (failed.length && failed.length === results.length) {
          setError(`Publishing failed: ${failed.map((f: { platform: string; error_message: string }) => `${f.platform} — ${f.error_message}`).join('; ')}`);
          return;
        }
        if (failed.length) {
          setSuccess(`Published, but ${failed.map((f: { platform: string; error_message: string }) => `${f.platform} failed (${f.error_message})`).join('; ')}`);
        } else {
          setSuccess('Published successfully');
        }
      } else {
        setSuccess(action === 'schedule' ? 'Post scheduled' : 'Draft saved');
      }

      setContent('');
      setSelectedPlatforms([]);
      setScheduledDate(null);
      setScheduleVariants({});
      setMedia([]);
    } catch (err) {
      setError('Network error creating post');
    } finally {
      setSubmitting(false);
    }
  };

  const characterCount = content.length;

  /** The text a platform will actually receive — its override, or the main caption. */
  const textFor = (platformId: string) => resolveText(platformId, content, variants);

  const selectedConfigs = selectedPlatforms.map(p => getPlatform(p)).filter(Boolean) as NonNullable<ReturnType<typeof getPlatform>>[];
  const limit = strictestCharLimit(selectedPlatforms);
  // Measured per platform against its own text, so writing a shorter X version clears the
  // block for X without touching the others.
  const overLimitPlatforms = selectedConfigs.filter(p => textFor(p.id).length > p.charLimit);
  // The limit the main caption is actually held to: the strictest among platforms that do NOT
  // have their own version. Give X its own text and LinkedIn's 3,000 takes over.
  const governingLimit =
    selectedConfigs
      .filter(p => !variants[p.id]?.trim())
      .reduce((min, p) => Math.min(min, p.charLimit), Infinity) || (limit ?? Infinity);
  const missingMediaPlatforms = selectedConfigs.filter(p => p.requiresMedia && media.length === 0);
  const ignoredMediaPlatforms = media.length > 0 ? selectedConfigs.filter(p => p.supportsMedia === false) : [];
  // A post that cannot succeed should not be submittable
  const blocked =
    submitting ||
    uploading ||
    !content.trim() ||
    selectedPlatforms.length === 0 ||
    overLimitPlatforms.length > 0 ||
    missingMediaPlatforms.length > 0;

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Create Post
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, value) => value && setMode(value)}
        >
          <ToggleButton value="manual">Manual</ToggleButton>
          <ToggleButton value="guided">Guided</ToggleButton>
        </ToggleButtonGroup>

        {/* Renders nothing when staging is empty, so the control never asks a question the
            page cannot answer. */}
        {mode === 'manual' && (
          <IdeaPicker
            onPick={(idea: StagedIdea) => {
              setFromIdea(idea.id);
              setIdeaVersion(idea.version || null);
              setIdeaTimes({
                scheduledFor: idea.scheduled_for || null,
                scheduleVariants: idea.schedule_variants || null,
              });
              setContent(idea.content);
              setSelectedPlatforms((idea.platforms || []) as SocialPlatform[]);
              setVariants(idea.content_variants || {});
              setMedia((idea.media_urls || []).map(url => ({
                id: url, url, mimeType: '', sizeBytes: 0,
              })));
              setSuccess('Loaded from Idea Staging — choose a day and time, or save it back.');
            }}
          />
        )}
      </Box>

      {mode === 'guided' && <GuidedPost />}

      {mode === 'manual' && (
      <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Post Content
              </Typography>

              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  placeholder="What's on your mind?"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  inputRef={textFieldRef}
                  sx={{ mb: 1 }}
                />
                <IconButton
                  onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
                  sx={{ position: 'absolute', right: 8, bottom: 16 }}
                  size="small"
                >
                  <EmojiIcon />
                </IconButton>
              </Box>

              <Popover
                open={Boolean(emojiAnchorEl)}
                anchorEl={emojiAnchorEl}
                onClose={() => setEmojiAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <EmojiPicker onEmojiClick={handleEmojiClick} />
              </Popover>

              {/* Per-platform counts: each network has a different ceiling, so one
                  number against Twitter's 280 was misleading. */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {characterCount.toLocaleString()} characters
                  </Typography>
                  {limit !== null && (
                    <Typography
                      variant="caption"
                      color={characterCount > limit ? 'error' : 'text.secondary'}
                    >
                      {/* Once a platform has its own text the strictest limit no longer
                          governs the main caption, so name the platform the count is for
                          rather than calling it "strictest" and being wrong. */}
                      {(() => {
                        const governing = selectedConfigs
                          .filter(p => !variants[p.id]?.trim())
                          .sort((a, b) => a.charLimit - b.charLimit)[0];
                        if (!governing) return `${characterCount.toLocaleString()} characters`;
                        const left = governing.charLimit - characterCount;
                        const others = selectedConfigs.filter(p => !variants[p.id]?.trim()).length > 1;
                        return `${left.toLocaleString()} left${others ? ` (${governing.name})` : ''}`;
                      })()}
                    </Typography>
                  )}
                </Box>

                {limit !== null && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (characterCount / governingLimit) * 100)}
                    color={
                      characterCount > governingLimit
                        ? 'error'
                        : characterCount > governingLimit * 0.9
                          ? 'warning'
                          : 'primary'
                    }
                    sx={{ height: 4, borderRadius: 2, mb: 1.5 }}
                  />
                )}

                {selectedConfigs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {selectedConfigs.map(p => {
                      // Counted against this platform's own text, so a shortened X version
                      // shows as within limit while the others keep the full caption.
                      const count = textFor(p.id).length;
                      const over = count > p.charLimit;
                      const hasOwn = Boolean(variants[p.id]?.trim());
                      return (
                        <Tooltip
                          key={p.id}
                          title={over
                            ? `${(count - p.charLimit).toLocaleString()} over ${p.name}'s ${p.charLimit.toLocaleString()} limit`
                            : hasOwn
                              ? `${p.name} has its own version — ${p.charLimit.toLocaleString()} character limit`
                              : `${p.charLimit.toLocaleString()} character limit`}
                        >
                          <Chip
                            size="small"
                            label={`${p.name} ${count.toLocaleString()}/${p.charLimit.toLocaleString()}${hasOwn ? ' ·' : ''}`}
                            sx={{
                              bgcolor: over ? 'error.main' : 'transparent',
                              color: over ? '#fff' : hasOwn ? getPlatformColor(p.id) : 'text.secondary',
                              border: over ? 'none' : '1px solid',
                              borderColor: getPlatformColor(p.id),
                              fontSize: '0.7rem',
                              fontWeight: hasOwn ? 600 : 400,
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}

                {/* The same editor the Edit dialog uses. Two copies of this drifted once
                    already — the dialog had none of it, so a post with a shorter X version
                    could not be saved and its X text was wiped on save. */}
                <PerPlatformCopy
                  platforms={selectedPlatforms}
                  content={content}
                  variants={variants}
                  onChange={setVariants}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Media */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: media.length ? 2 : 0 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ImageIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading…' : 'Add image or video'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime"
                    onChange={handleFileSelect}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Up to 15 MB
                  </Typography>
                </Box>

                {uploading && <LinearProgress sx={{ mt: 1 }} />}

                {media.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {media.map(item => (
                      <Box
                        key={item.id}
                        sx={{
                          position: 'relative',
                          width: 96,
                          height: 96,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        {item.mimeType.startsWith('video/') ? (
                          <Box sx={{
                            width: '100%', height: '100%', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', bgcolor: 'action.hover',
                          }}>
                            <Typography variant="caption">Video</Typography>
                          </Box>
                        ) : (
                          <Box component="img" src={thumbnailUrl(item.url, 320)} alt="" loading="lazy" decoding="async" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveMedia(item.id)}
                          sx={{
                            position: 'absolute', top: 2, right: 2,
                            bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                {selectedPlatforms.includes('instagram') && media.length > 0 && (
                  <InstagramImageCheck
                    urls={media.map(m => m.url)}
                    onReplace={(oldUrl, fitted) =>
                      setMedia(prev => prev.map(m => (m.url === oldUrl
                        ? { id: fitted.id, url: fitted.url, mimeType: fitted.mimeType, sizeBytes: fitted.sizeBytes }
                        : m)))
                    }
                  />
                )}

                {missingMediaPlatforms.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {missingMediaPlatforms.map(p => p.name).join(', ')} requires an image or video.
                  </Alert>
                )}

                {ignoredMediaPlatforms.length > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Media will not be attached for {ignoredMediaPlatforms.map(p => p.name).join(', ')} —
                    those post as text only.
                  </Alert>
                )}
              </Box>

              {/* Review before publishing: the ask that prompted this was not being able
                  to see a full draft or how it would lay out. */}
              <Box sx={{ mb: 3 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={() => setShowPreview(!showPreview)}
                  disabled={!content.trim() || selectedPlatforms.length === 0}
                >
                  {showPreview ? 'Hide preview' : 'Preview post'}
                </Button>

                {showPreview && (
                  <Box sx={{ mt: 2 }}>
                    <PostPreview
                      content={content}
                      contentVariants={variants}
                      platforms={selectedPlatforms}
                      mediaUrls={media.map(m => m.url)}
                      targets={targets}
                    />
                  </Box>
                )}
              </Box>

              <Typography variant="h6" gutterBottom>
                Schedule Post (Optional)
              </Typography>

              {/* The same picker as the Edit and Schedule dialogs, so a post can be given a
                  time per network when it is created rather than only by editing it
                  afterwards. It carries the timing advice and the same-day warning too. */}
              <PlatformSchedule
                platforms={selectedPlatforms}
                base={scheduledDate}
                variants={scheduleVariants}
                onBaseChange={setScheduledDate}
                onVariantsChange={setScheduleVariants}
              />
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Platforms
              </Typography>

              <FormGroup>
                {platforms.map((platform) => (
                  <Box key={platform.value}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedPlatforms.includes(platform.value)}
                          onChange={() => handlePlatformChange(platform.value)}
                        />
                      }
                      label={platform.label}
                    />
                    {platform.value === 'linkedin' && selectedPlatforms.includes('linkedin') && getLinkedInDestination() && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1, mb: 1 }}>
                        {getLinkedInDestination()}
                      </Typography>
                    )}
                  </Box>
                ))}
              </FormGroup>

              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<SendIcon />}
                  disabled={blocked}
                  onClick={() => handleSubmit('publish')}
                >
                  Publish Now
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ScheduleIcon />}
                  disabled={blocked || !scheduledDate}
                  onClick={() => handleSubmit('schedule')}
                >
                  Schedule Post
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<SaveIcon />}
                  // No platform needed to save an idea: the point of staging is to capture
                  // the copy and media before deciding where it goes. Scheduling and
                  // publishing still require at least one, which the other buttons enforce.
                  disabled={submitting || uploading || (!content.trim() && media.length === 0)}
                  onClick={() => handleSubmit('save')}
                >
                  Save as Draft
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
      </>
      )}
    </Box>
  );
};
